import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  ITask,
  ITaskTemplate,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { compact, flatten, keyBy, uniq } from 'lodash';
import { getItemDisplayName, resolveExpTitle } from '../../../../helpers/adminName';
import {
  buildAssigneeBounceItemKeys,
  messageCoveredByAssigneeBounce
} from '../../../../../../services/experiences/clearAssigneeBounce';
import { getChannelsForSentItem } from '../sentItemChannels';
import { groupOpenErroredAttempts } from '../../../../../../services/experiences/concreteMessageAttempts';
import type { ExperienceProblemData, SentItemErrorData } from '../../InstanceProblems';

export const collectSentItemIssues = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  nowMillis: number = Date.now()
): ExperienceProblemData[] => {
  // find all errored items that are not set to be ignored
  // see if there's a newer successful attempt made
  // see if e.g. tasks are already completed.
  // if someone received a reminder for a task that errored, it's also to be ignored
  // see if the failed items still exist in the template

  const messagesByTplId = keyBy(tpl.data.messages.other, (m) => m.id);
  // SC-33594: a bounced / send-time-suppressed email is already surfaced as a
  // dedicated ASSIGNEE_BOUNCE row (from instanceErrors[]). Suppress the generic
  // SENT_ITEM twin here so one failed email isn't counted as two issues. This
  // must cover BOTH bounce-writer paths: the Postmark webhook stores the message
  // as status='BOUNCED', while the send-time 406 suppression path (an address
  // already on Postmark's suppression list) stores it as status='ERROR' — the
  // earlier BOUNCED-only guard missed that second case. The check is
  // existence-gated (see buildAssigneeBounceItemKeys) so an errored send with no
  // matching bounce record still surfaces, and ignore-agnostic so a
  // retried/ignored bounce never makes the twin reappear. OK/QUEUED/ignored
  // messages already returned above, so no delivered send is ever hidden.
  const assigneeBounceKeys = buildAssigneeBounceItemKeys(d.data.instanceErrors);
  const sentItemIssues: SentItemErrorData[] = [];
  // SC-36146: every failed attempt is its own messages[] row (a resend appends a
  // new row rather than updating the failed one), so an item that failed N
  // times used to show N identical issues — Retry cleared one and admins kept
  // retrying the twins, sending the item again each time. One row per item +
  // channel now (built from the newest failure, carrying every failed msgId),
  // and failures already superseded by a later delivered attempt — or covered by
  // a retry still in flight — no longer surface at all.
  groupOpenErroredAttempts(d.data.messages, nowMillis).forEach(({ latest: msg, msgIds }) => {
    if (messageCoveredByAssigneeBounce(msg, assigneeBounceKeys)) {
      return;
    }

    if (msg.type === 'EXPERIENCE_MESSAGE') {
      // check if the message and/or tasks are still around. if not, ignore the error
      // check if the tasks inside are already completed - if yes, don't flag
      // this as an error
      if (msg.scope?.msgId) {
        const messageTpl = messagesByTplId[msg.scope?.msgId];
        if (!messageTpl) {
          console.warn('Message tpl not found');
          return;
        }
        const connectedTaskIds = uniq(msg.scope.taskIds); // for some reason these can appear duplicated!
        let humanReadableTitle = resolveExpTitle(getItemDisplayName(messageTpl), tpl.data.title);
        if (connectedTaskIds.length) {
          humanReadableTitle += ` + ${connectedTaskIds.length} ${
            connectedTaskIds.length === 1 ? 'task' : 'tasks'
          }`;
        }

        const err: SentItemErrorData = {
          type: 'SENT_ITEM',
          tpl,
          instance: d,
          id: msg.msgId,
          humanReadableTitle,
          includedItems: [],
          itemTplId: messageTpl.id,
          actor: messageTpl.actor,
          message: msg,
          channels: getChannelsForSentItem(messageTpl.channels, msg),
          groupedMessageIds: msgIds
        };
        sentItemIssues.push(err);
      }
    }
    if (msg.type === 'TASK_START') {
      const tasks = compact(
        flatten(
          msg.scope?.taskIds.map((taskId) =>
            d.data.tasks.find((task) => task.id === taskId)
          ) || []
        )
      );
      const notCompletedTasks = tasks.filter(
        (task) =>
          task.status !== TaskStatus.COMPLETE &&
          task.status !== TaskStatus.SKIPPED
      );
      const notCompletedTasksAndTheirTpls = notCompletedTasks.map((task) => ({
        task,
        tpl:
          tpl.data.tasks.find((taskTpl) => taskTpl.id === task.templateId) ||
          null
      }));
      const withoutRemovedTaskTpls = notCompletedTasksAndTheirTpls.filter(
        (x): x is { task: ITask; tpl: ITaskTemplate } => !!x.tpl
      );
      if (!withoutRemovedTaskTpls.length) {
        return;
      }
      const firstTask = withoutRemovedTaskTpls[0];
      const otherTasks = withoutRemovedTaskTpls.slice(1);
      let humanReadableTitle = resolveExpTitle(getItemDisplayName(firstTask.tpl), tpl.data.title);
      if (otherTasks.length) {
        humanReadableTitle += ` + ${otherTasks.length} ${
          otherTasks.length === 1 ? 'task' : 'tasks'
        }`;
      }

      const err: SentItemErrorData = {
        type: 'SENT_ITEM',
        tpl,
        instance: d,
        id: msg.msgId,
        humanReadableTitle,
        includedItems: [],
        itemTplId: firstTask.tpl.id,
        actor: firstTask.tpl.actor,
        message: msg,
        channels: getChannelsForSentItem(firstTask.tpl.channels, msg),
        groupedMessageIds: msgIds
      };
      sentItemIssues.push(err);
    }
  });
  return sentItemIssues;
};
